import { Children, cloneElement, isValidElement, useEffect, useRef } from "react";

// Zero-dependency scroll reveal (IntersectionObserver + CSS transitions; see index.css ".reveal").
function useInViewClass() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -60px 0px", threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export default function Reveal({ children, delay = 0, y = 28, className = "", as = "div", style, ...rest }) {
  const ref = useInViewClass();
  const Tag = as;
  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`}
      style={{ "--reveal-delay": `${delay}s`, "--reveal-y": `${y}px`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function Stagger({ children, className = "", stagger = 0.07, as = "div", ...rest }) {
  const ref = useInViewClass();
  const Tag = as;
  let i = 0;
  const kids = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const idx = i++;
    return cloneElement(child, { style: { ...(child.props.style || {}), "--stagger-i": idx } });
  });
  return (
    <Tag ref={ref} className={`stagger ${className}`} style={{ "--stagger-step": `${stagger}s` }} {...rest}>
      {kids}
    </Tag>
  );
}

export function StaggerItem({ children, className = "", as = "div", ...rest }) {
  const Tag = as;
  return (
    <Tag className={`stagger-item ${className}`} {...rest}>
      {children}
    </Tag>
  );
}
